'use client';

/**
 * Per-session chat hook for the parallel sessions page.
 *
 * Streams are owned by a MODULE-LEVEL run manager, not the component:
 *  - the SSE reader keeps running when the pane unmounts
 *  - finished answers are written straight to disk history even with no
 *    pane mounted; reopened panes reattach to in-flight streams
 *  - per-slug MESSAGE QUEUE: sends typed while a run is active are queued
 *    and dispatched in order when the run finishes (classic-chat parity)
 *  - per-slug cumulative usage feeds the session status bar ($ / tokens)
 *
 * `main = true` targets the gateway's MAIN session (/chat, /abort) — the
 * pinned General entry — with identical behavior.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useChatHistory } from '@/lib/useChatHistory';
import { gateway } from '@/lib/api';
import type { ChatMessage, ChatImage, ToolCall, StreamEvent, TokenUsage, MessagePart } from './types';

const GW = '/api/gw';

interface LiveRun {
  slug: string;
  assistantMsgId: string;
  text: string;
  tools: ToolCall[];
  parts: MessagePart[];
  thinking: boolean;
  done: boolean;
  error: string | null;
  usage: TokenUsage | null;
  controller: AbortController;
  listeners: Set<(r: LiveRun) => void>;
  lastNotify: number;
  main: boolean;
}

interface QueuedSend { text: string; images?: ChatImage[]; userMsgId: string }

const runs = new Map<string, LiveRun>();
const queues = new Map<string, QueuedSend[]>();
// placeholder ids the orphan-recovery effect has already handled — so it attaches
// /polls a given dead/in-flight bubble ONCE instead of re-firing every render
// (which reset the bubble = the "session keeps refreshing" churn).
const orphanHandled = new Set<string>();
// cumulative token usage per slug (page-load lifetime — classic-chat parity)
const usageTotals = new Map<string, { input: number; output: number; cacheRead: number; cacheWrite: number }>();
const usageListeners = new Set<() => void>();

export function getSessionUsage(slug: string) {
  return usageTotals.get(slug) || { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 };
}
export function onSessionUsage(fn: () => void): () => void {
  usageListeners.add(fn);
  return () => usageListeners.delete(fn);
}
export function getQueueLength(slug: string): number {
  return (queues.get(slug) || []).length;
}

// ── live "is this session running" — for the rail's working indicator ───────
// The gateway's polled state flickers to 'idle' during long tool calls
// (isStreaming briefly false). The LOCAL run stays active until run_end, so the
// rail ORs this in to keep the spinner steady through tool execution.
const runChangeListeners = new Set<() => void>();
export function isSessionRunning(slug: string): boolean {
  const r = runs.get(slug);
  return !!r && !r.done;
}
export function onRunChange(fn: () => void): () => void {
  runChangeListeners.add(fn);
  return () => runChangeListeners.delete(fn);
}
function notifyRunChange(): void {
  runChangeListeners.forEach((l) => { try { l(); } catch {} });
}

function notify(run: LiveRun, force = false) {
  const now = Date.now();
  if (!force && now - run.lastNotify < 50) return;
  run.lastNotify = now;
  run.listeners.forEach((l) => { try { l(run); } catch {} });
}

function buildFinalParts(run: LiveRun): MessagePart[] {
  let parts = [...run.parts];
  if (run.text.trim()) parts.push({ type: 'text', content: run.text });
  return parts.map((p) =>
    p.type === 'tool' && p.tool.output && p.tool.output.length > 4000
      ? { ...p, tool: { ...p.tool, output: p.tool.output.slice(0, 4000) + '\n... [truncated]' } }
      : p,
  );
}

/**
 * A finished run must never leave a tool in 'running' — that block would spin
 * forever in the UI (2026-07-03 scar: a dropped tool_end persisted a spinner
 * into history; only a page reload cleared it). Called in every run's finally.
 */
function finalizeRunningTools(run: LiveRun): void {
  const aborted = run.controller.signal.aborted;
  run.parts = run.parts.map((p) => {
    if (p.type === 'tool' && p.tool.status === 'running') {
      return {
        ...p,
        tool: {
          ...p.tool,
          status: aborted ? ('error' as const) : ('done' as const),
          output: p.tool.output || (aborted ? '[stopped]' : '[finished — output not captured]'),
        },
      };
    }
    return p;
  });
}

/** Commit the finished (or aborted) answer to disk history directly —
 *  works even when the pane is closed. */
async function persistFinal(run: LiveRun): Promise<void> {
  try {
    const res: any = await gateway.chatHistory.read(run.slug);
    const msgs: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
    const parts = buildFinalParts(run);
    if (!run.text && parts.length === 0) {
      const filtered = msgs.filter((m) => m.id !== run.assistantMsgId);
      if (filtered.length !== msgs.length) await gateway.chatHistory.write(run.slug, filtered as unknown[]);
      return;
    }
    const finalMsg: ChatMessage = {
      id: run.assistantMsgId, role: 'assistant', content: run.text,
      parts: parts.length > 0 ? parts : undefined, timestamp: Date.now(),
    };
    const idx = msgs.findIndex((m) => m.id === run.assistantMsgId);
    if (idx >= 0) msgs[idx] = { ...msgs[idx], ...finalMsg };
    else msgs.push(finalMsg);
    await gateway.chatHistory.write(run.slug, msgs as unknown[]);
  } catch { /* gateway down — mounted-pane state path still applies */ }
}

/** Pull a gateway-recovered reply into the run. When a run finishes while the
 *  client is disconnected AND the session has hibernated / the gateway
 *  restarted, the reattach `/stream` carries a bare `idle` with NO event replay
 *  (the in-memory journal is gone) — so the live bubble would sit empty even
 *  though the gateway's `persistRunToHistory` already saved the full reply under
 *  the SAME placeholder id. Adopt that saved reply so the bubble fills instead
 *  of staying blank or erroring. Returns true if content was adopted. */
async function adoptFromHistory(run: LiveRun): Promise<boolean> {
  try {
    const res: any = await gateway.chatHistory.read(run.slug);
    const msgs: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
    const msg = msgs.find((m) => m.id === run.assistantMsgId);
    if (!msg || msg.role !== 'assistant') return false;
    if (Array.isArray(msg.parts) && msg.parts.length > 0) { run.parts = msg.parts as MessagePart[]; run.text = ''; return true; }
    if (typeof msg.content === 'string' && msg.content.trim()) { run.text = msg.content; run.parts = []; return true; }
    return false;
  } catch { return false; }
}

// STUCK-RUN WATCHDOG. A run can be left showing "Working…" forever if the gateway
// loses it without sending an end signal — e.g. the turn died on an API
// timeout/overload and the session hibernated, so no run_end ever reached us
// (the real kranes-ca symptom: spinner spinning on a dead, idle session). Every
// 8s we check each live run: if it has had NO events for >30s AND the gateway
// reports the session is NOT working (state!=='working' && !isStreaming), the run
// is dead — finalize it. We first try to adopt a reply that may have landed in
// history; if there's none, we surface a clear, retry-able error. The gateway
// reports state='working' for the ENTIRE run including long auto-resume/rate-limit
// backoffs, so a legitimately-waiting run is never falsely killed.
if (typeof window !== 'undefined') {
  // SINGLE-INSTANCE: a module-level setInterval is re-created on every Next.js
  // HMR hot-reload of this file; without clearing the previous one they STACK,
  // and after a few edits dozens are polling /sessions/<slug> at once (the
  // "session always refreshes" storm). Clear any prior timer before arming.
  const g = window as unknown as { __beastRunWatchdog?: ReturnType<typeof setInterval> };
  if (g.__beastRunWatchdog) clearInterval(g.__beastRunWatchdog);
  g.__beastRunWatchdog = setInterval(async () => {
    for (const run of Array.from(runs.values())) {
      if (run.done || run.main) continue;
      if (Date.now() - run.lastNotify < 30_000) continue; // recent activity → fine
      let working = true;
      try {
        const res = await fetch(`${GW}/sessions/${run.slug}`);
        if (res.ok) { const s = (await res.json())?.session; working = !s || s.state === 'working' || !!s.isStreaming; }
      } catch { continue; } // gateway unreachable — don't kill the run, just wait
      if (working || run.done) continue;
      // Dead run: no events for 30s AND the gateway isn't working on it.
      const adopted = await adoptFromHistory(run);
      if (!adopted) run.error = 'This turn ended without a reply (likely an API timeout or overload). Re-send to continue.';
      run.done = true;
      run.thinking = false;
      try { run.controller.abort(); } catch { /* stops the dangling stream reader */ }
      await persistFinal(run);
      notify(run, true);
      if (runs.get(run.slug) === run) runs.delete(run.slug);
      notifyRunChange();
    }
  }, 8_000);
}

/** Append the queued user msg + placeholder to disk history directly
 *  (used when the next queued send starts while the pane is unmounted). */
async function persistQueueStart(slug: string, userMsg: ChatMessage, placeholder: ChatMessage): Promise<void> {
  try {
    const res: any = await gateway.chatHistory.read(slug);
    const msgs: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
    if (!msgs.some((m) => m.id === userMsg.id)) msgs.push(userMsg);
    if (!msgs.some((m) => m.id === placeholder.id)) msgs.push(placeholder);
    await gateway.chatHistory.write(slug, msgs as unknown[]);
  } catch {}
}

async function startRun(slug: string, message: string, images: ChatImage[] | undefined, assistantMsgId: string, main: boolean, context?: string): Promise<void> {
  const run: LiveRun = {
    slug, assistantMsgId, text: '', tools: [], parts: [],
    thinking: true, done: false, error: null, usage: null,
    controller: new AbortController(), listeners: new Set(), lastNotify: Date.now(), main,
  };
  runs.set(slug, run);
  notifyRunChange();

  const handleEvent = (event: StreamEvent) => {
    switch (event.type) {
      case 'text_delta':
        run.thinking = false;
        run.text += event.text;
        notify(run);
        break;
      case 'thinking': {
        run.thinking = true;
        // Thought process is a first-class part — never dropped, never mixed into text
        if (event.text) {
          const last = run.parts[run.parts.length - 1];
          if (last && last.type === 'thinking') {
            run.parts = [...run.parts.slice(0, -1), { type: 'thinking', content: last.content + event.text }];
          } else {
            run.parts = [...run.parts, { type: 'thinking', content: event.text }];
          }
        }
        notify(run);
        break;
      }
      case 'tool_start': {
        run.thinking = false;
        if (run.text.trim()) {
          run.parts = [...run.parts, { type: 'text', content: run.text }];
          run.text = '';
        }
        const tool: ToolCall = { id: event.id, name: event.name, input: event.input, status: 'running' };
        run.tools = [...run.tools, tool];
        run.parts = [...run.parts, { type: 'tool', tool }];
        notify(run, true);
        break;
      }
      case 'tool_end':
        run.tools = run.tools.map((t) =>
          t.id === event.id ? { ...t, output: event.output, status: event.error ? 'error' as const : 'done' as const } : t);
        run.parts = run.parts.map((p) =>
          p.type === 'tool' && p.tool.id === event.id
            ? { ...p, tool: { ...p.tool, output: event.output, status: event.error ? 'error' as const : 'done' as const } }
            : p);
        notify(run, true);
        break;
      case 'done':
        if (event.usage) {
          run.usage = event.usage;
          const t = usageTotals.get(slug) || { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 };
          usageTotals.set(slug, {
            input: t.input + (event.usage.input || 0),
            output: t.output + (event.usage.output || 0),
            cacheRead: t.cacheRead + (event.usage.cacheRead || 0),
            cacheWrite: t.cacheWrite + (event.usage.cacheWrite || 0),
          });
          usageListeners.forEach((l) => { try { l(); } catch {} });
          window.dispatchEvent(new CustomEvent('beast:usage', { detail: event.usage }));
        }
        break;
      case 'error':
        run.error = event.message;
        notify(run, true);
        break;
    }
  };

  // ── transport with reattach: a dropped connection is NOT the end of the
  // run. The gateway journals every event with a sequence number; we
  // reconnect to /stream?since=<lastSeq> and replay what we missed.
  let lastSeq = 0;
  let sawEnd = false;
  const readStream = async (res: Response) => {
    const reader = res.body?.getReader();
    if (!reader) throw new Error('No response stream');
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;
        try {
          const evt = JSON.parse(data);
          if (typeof evt.seq === 'number') lastSeq = Math.max(lastSeq, evt.seq);
          if (evt.type === 'run_end' || evt.type === 'idle') { sawEnd = true; continue; }
          if (evt.type === 'run_start' || evt.type === 'attach' || evt.type === 'auto_resume' || evt.type === 'keepalive') continue;
          handleEvent(evt as StreamEvent);
        } catch { /* skip malformed */ }
      }
    }
  };
  try {
    const payload: Record<string, unknown> = { message };
    if (images && images.length > 0) payload.images = images;
    // optional model-only grounding (gateway injects this as a separate
    // "[Project Context]" block — it's NOT echoed into the visible bubble)
    if (context) payload.context = context;
    try {
      const res = await fetch(main ? `${GW}/chat` : `${GW}/sessions/${slug}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: run.controller.signal,
      });
      if (!res.ok) throw new Error(`Gateway error: ${res.status}`);
      await readStream(res);
    } catch (firstErr) {
      if (run.controller.signal.aborted || main) throw firstErr;
      // fall through to the reattach loop below
    }
    // reattach loop (project sessions): keep reconnecting while the gateway
    // says the run is alive — transport errors become invisible
    if (!main) {
      let attempts = 0;
      while (!sawEnd && !run.controller.signal.aborted && attempts < 6) {
        attempts++;
        await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** (attempts - 1), 15000)));
        try {
          const res = await fetch(`${GW}/sessions/${slug}/stream?since=${lastSeq}`, { signal: run.controller.signal });
          if (!res.ok) continue;
          attempts = 0; // successful (re)connect resets the backoff budget
          await readStream(res);
        } catch { /* next attempt */ }
      }
      // Reattach ended (graceful `idle`, run_end, or 6 failed attempts) but we
      // never received the reply text — the run finished while we were detached
      // and the session hibernated/restarted, so no journal was left to replay.
      // The gateway's history-recovery has it; adopt it so the bubble fills.
      if (!run.controller.signal.aborted && !run.text && run.parts.length === 0) {
        await new Promise((r) => setTimeout(r, 400)); // let run_end's history-persist land
        const adopted = await adoptFromHistory(run);
        if (!adopted && !sawEnd) {
          run.error = 'Lost connection to the gateway — the agent may still be working. It will reattach when you reload.';
        }
      }
    }
  } catch (err: unknown) {
    if (!(err instanceof Error && err.name === 'AbortError')) {
      run.error = err instanceof Error ? err.message : 'Connection failed';
    }
  } finally {
    run.done = true;
    run.thinking = false;
    finalizeRunningTools(run);
    await persistFinal(run);
    notify(run, true);
    runs.delete(slug);
    notifyRunChange();

    // dispatch the next queued message (order preserved, even if no pane)
    const q = queues.get(slug);
    if (q && q.length > 0 && !run.controller.signal.aborted) {
      const next = q.shift()!;
      const placeholderId = `msg_${Date.now() + 1}`;
      const userMsg: ChatMessage = { id: next.userMsgId, role: 'user', content: next.text, timestamp: Date.now() };
      const placeholder: ChatMessage = { id: placeholderId, role: 'assistant', content: '', timestamp: Date.now() };
      await persistQueueStart(slug, userMsg, placeholder);
      queueStartListeners.forEach((l) => { try { l(slug, placeholderId); } catch {} });
      startRun(slug, next.text, next.images, placeholderId, main);
    } else if (run.controller.signal.aborted) {
      queues.delete(slug); // user hit Stop — drop the queue, classic behavior
    }
  }
}

/** Attach to a run THIS page didn't start (session working in the background,
 *  after a reload or gateway restart) so its live progress shows in the bubble
 *  and the final reply lands — without a POST (the run already exists). Streams
 *  from the gateway's CURRENT seq (no replay of prior turns) for live activity,
 *  then takes the authoritative full reply from history at the end. Deliberately
 *  does NOT write history itself: the gateway's run_end safety-net owns that, so
 *  partial streamed text can never overwrite the saved reply. */
async function attachRun(slug: string, assistantMsgId: string): Promise<void> {
  if (runs.get(slug)) return;
  const run: LiveRun = {
    slug, assistantMsgId, text: '', tools: [], parts: [],
    thinking: true, done: false, error: null, usage: null,
    controller: new AbortController(), listeners: new Set(), lastNotify: Date.now(), main: false,
  };
  runs.set(slug, run);
  notifyRunChange();

  let lastSeq = 0;
  let sawEnd = false;
  const handleEvent = (event: StreamEvent) => {
    switch (event.type) {
      case 'text_delta': run.thinking = false; run.text += event.text; notify(run); break;
      case 'thinking': {
        run.thinking = true;
        if (event.text) {
          const last = run.parts[run.parts.length - 1];
          run.parts = last && last.type === 'thinking'
            ? [...run.parts.slice(0, -1), { type: 'thinking', content: last.content + event.text }]
            : [...run.parts, { type: 'thinking', content: event.text }];
        }
        notify(run); break;
      }
      case 'tool_start': {
        run.thinking = false;
        if (run.text.trim()) { run.parts = [...run.parts, { type: 'text', content: run.text }]; run.text = ''; }
        const tool: ToolCall = { id: event.id, name: event.name, input: event.input, status: 'running' };
        run.tools = [...run.tools, tool];
        run.parts = [...run.parts, { type: 'tool', tool }];
        notify(run, true); break;
      }
      case 'tool_end':
        run.tools = run.tools.map((t) => t.id === event.id ? { ...t, output: event.output, status: event.error ? 'error' as const : 'done' as const } : t);
        run.parts = run.parts.map((p) => p.type === 'tool' && p.tool.id === event.id ? { ...p, tool: { ...p.tool, output: event.output, status: event.error ? 'error' as const : 'done' as const } } : p);
        notify(run, true); break;
      case 'done': if (event.usage) run.usage = event.usage; break;
      case 'error': run.error = event.message; notify(run, true); break;
    }
  };
  const readStream = async (res: Response) => {
    const reader = res.body?.getReader();
    if (!reader) return;
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;
        try {
          const evt = JSON.parse(data);
          if (typeof evt.seq === 'number') lastSeq = Math.max(lastSeq, evt.seq);
          if (evt.type === 'run_end' || evt.type === 'idle') { sawEnd = true; continue; }
          if (evt.type === 'run_start' || evt.type === 'attach' || evt.type === 'auto_resume' || evt.type === 'keepalive') continue;
          handleEvent(evt as StreamEvent);
        } catch { /* skip malformed */ }
      }
    }
  };

  try {
    // start at the session's current seq so we stream live from now (not a
    // replay of every buffered prior turn).
    try {
      const res = await fetch(`${GW}/sessions/${slug}`, { signal: run.controller.signal });
      if (res.ok) lastSeq = Number((await res.json())?.session?.lastSeq) || 0;
    } catch { /* since=0 fallback is acceptable */ }
    let attempts = 0;
    while (!sawEnd && !run.controller.signal.aborted && attempts < 8) {
      try {
        const res = await fetch(`${GW}/sessions/${slug}/stream?since=${lastSeq}`, { signal: run.controller.signal });
        if (res.ok) { attempts = 0; await readStream(res); } else attempts++;
      } catch { attempts++; }
      if (!sawEnd && !run.controller.signal.aborted) await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempts, 8000)));
    }
    // Authoritative full reply (the gateway persists it ~4s after run_end);
    // replaces any partial we streamed. Retry briefly to catch that write.
    if (!run.controller.signal.aborted) {
      for (let i = 0; i < 6; i++) { if (await adoptFromHistory(run)) break; await new Promise((r) => setTimeout(r, 1500)); }
    }
  } catch { /* ignore — finally cleans up */ }
  finally {
    run.done = true;
    run.thinking = false;
    finalizeRunningTools(run);
    notify(run, true);
    runs.delete(slug);
    notifyRunChange();
  }
}

// panes listen so they can add the placeholder to mounted state when a
// queued send kicks off
const queueStartListeners = new Set<(slug: string, placeholderId: string) => void>();

// Authoritative disk delete of one message by id. Disk is the record future
// sessions read from, and useChatHistory's merge-poll / merge-on-write re-add
// anything that's on disk but not in local state — so we must scrub disk here.
/** Visible TEXT of a message — text parts only, mirroring entryText() in
 *  shared/forget-message.cjs so the UI message can be matched to its entry in
 *  the SDK context tree. */
function textPartsOf(m: ChatMessage): string {
  if (m.parts && m.parts.length) {
    return m.parts.filter((p) => p.type === 'text').map((p) => (p as { content: string }).content).join('');
  }
  return m.content || '';
}

async function removeMessageFromDisk(slug: string, msgId: string) {
  try {
    const res: any = await gateway.chatHistory.read(slug);
    const disk: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
    const next = disk.filter((m) => m?.id !== msgId);
    if (next.length !== disk.length) await gateway.chatHistory.write(slug, next as unknown[]);
  } catch { /* ignore */ }
}

/**
 * Start a turn in a project's session from OUTSIDE any mounted chat pane.
 *
 * VibeRecordInbox lives in the root layout, not inside the session pane, and it must be
 * able to actually RUN a delivered task — Boss: "it should automatically execute as per
 * task". This is the same path the queue dispatcher uses when a queued message starts with
 * no pane mounted (persist user + placeholder to disk, tell any listening pane, run), so a
 * task that arrives while the project chat is closed behaves identically to one typed into it.
 *
 * If a run is already live on that slug the task is QUEUED rather than steered: a delivered
 * task is its own piece of work, not a correction to whatever is already running.
 */
export async function sendToSession(slug: string, text: string): Promise<void> {
  const message = (text || '').trim();
  if (!message) return;
  const userMsgId = `msg_${Date.now()}`;

  if (runs.get(slug)) {
    const q = queues.get(slug) || [];
    q.push({ text: message, userMsgId });
    queues.set(slug, q);
    notifyRunChange();
    return;
  }

  const placeholderId = `msg_${Date.now() + 1}`;
  const userMsg: ChatMessage = { id: userMsgId, role: 'user', content: message, timestamp: Date.now() };
  const placeholder: ChatMessage = { id: placeholderId, role: 'assistant', content: '', timestamp: Date.now() };
  await persistQueueStart(slug, userMsg, placeholder);
  queueStartListeners.forEach((l) => { try { l(slug, placeholderId); } catch {} });
  void startRun(slug, message, undefined, placeholderId, false);
}

export function useSessionChat(slug: string, main = false) {
  const [messages, setMessages, historyHydrated] = useChatHistory<ChatMessage>(slug);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // History can contain tool blocks frozen in 'running' (stream interrupted
  // mid-tool in a past life) — they spin forever and read as 'still working'.
  // Same cleanup the classic chat does on load.
  useEffect(() => {
    if (!historyHydrated) return;
    if (runs.get(slug)) return; // never touch a genuinely live run's blocks
    setMessages((prev) => {
      let changed = false;
      const fixed = prev.map((m) => {
        if (m.role !== 'assistant' || !m.parts) return m;
        const parts = m.parts.map((p) => {
          if (p.type === 'tool' && p.tool.status === 'running') {
            changed = true;
            return { ...p, tool: { ...p.tool, status: 'error' as const, output: p.tool.output || '[interrupted]' } };
          }
          return p;
        });
        return changed ? { ...m, parts } : m;
      });
      return changed ? fixed : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyHydrated, slug]);

  // LIVE MERGE-POLL. Messages are appended to the history FILE by writers that
  // are not this pane: the voice-conversation mirror, voice-delegated tasks and
  // their work log, and VibeRecordInbox's delivery cards. Without this, state is
  // only ever hydrated ON MOUNT — so an external message sits on disk, correctly
  // merged by useChatHistory's merge-on-write, and is invisible until the user
  // reloads or switches projects. 2026-10-23: a VibeRecord task delivered, the
  // card was written, the audit log said so, and Boss asked "where is it?" —
  // because SessionChat (every project chat) never had this poll. The legacy
  // Chat.tsx/useChat had it; useSessionChat did not.
  //
  // Skips while a run is live (the stream owns the list) and while the tab is
  // hidden. Appends at the END — no re-sort, because reordering on every tick
  // made the view jump (Boss: "stop refreshing the chat").
  useEffect(() => {
    if (!historyHydrated) return;
    const iv = setInterval(async () => {
      if (runs.get(slug) || document.hidden) return;
      try {
        const res: any = await gateway.chatHistory.read(slug);
        const disk: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
        if (!disk.length) return;
        setMessages((prev) => {
          const have = new Set(prev.map((m) => m.id));
          const fresh = disk.filter((m) => m?.id && !have.has(m.id));
          // voicetask_* work logs are UPDATED IN PLACE on disk while the
          // delegated task runs — take the disk copy when it changed.
          const diskById = new Map(disk.map((m) => [m.id, m]));
          let updated = false;
          const next = prev.map((m) => {
            if (!m.id?.startsWith('voicetask_')) return m;
            const d = diskById.get(m.id);
            if (d && (d.content !== m.content
              || JSON.stringify(d.parts || null) !== JSON.stringify(m.parts || null))) { updated = true; return d; }
            return m;
          });
          if (!fresh.length && !updated) return prev;
          return [...next, ...fresh];
        });
      } catch { /* gateway briefly down — next tick */ }
    }, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyHydrated, slug]);

  const [, bump] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [lastFailed, setLastFailed] = useState<string | null>(null);

  // subscribe to this slug's live run (reattaches after pane switches)
  useEffect(() => {
    const r = runs.get(slug);
    if (!r) return;
    const listener = (rr: LiveRun) => {
      bump((x) => x + 1);
      if (rr.error) { setError(rr.error); setLastFailed(null); }
      if (rr.done) {
        const parts = buildFinalParts(rr);
        if (rr.text || parts.length > 0) {
          setMessages((prev) => prev.map((m) =>
            m.id === rr.assistantMsgId
              ? { ...m, content: rr.text, parts: parts.length > 0 ? parts : undefined, timestamp: Date.now() }
              : m));
        } else {
          setMessages((prev) => prev.filter((m) => m.id !== rr.assistantMsgId));
        }
      }
    };
    r.listeners.add(listener);
    bump((x) => x + 1);
    return () => { r.listeners.delete(listener); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, runs.get(slug)]);

  // queued sends starting → make sure the placeholder is in mounted state
  useEffect(() => {
    const l = (s: string, placeholderId: string) => {
      if (s !== slug) return;
      setMessages((prev) => prev.some((m) => m.id === placeholderId)
        ? prev
        : [...prev, { id: placeholderId, role: 'assistant' as const, content: '', timestamp: Date.now() }]);
      bump((x) => x + 1);
    };
    queueStartListeners.add(l);
    return () => { queueStartListeners.delete(l); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Orphan-run recovery. The live-run machinery only drives a bubble for a run
  // THIS page started. But a pane can mount over a trailing EMPTY assistant
  // placeholder whose run is owned elsewhere — after a gateway restart, a window
  // reload, or just opening a session that's working in the background. With no
  // local LiveRun that bubble sits blank, which reads as "stalled / nothing
  // happening". So: detect the orphan, ask the gateway its state, and either
  // ATTACH to the live stream (shows tools/text as it works) or, if it already
  // finished, pull the saved reply from history. Skips `main` (General chat has
  // its own path) and bails the moment a real local run takes over.
  useEffect(() => {
    if (!historyHydrated || main || runs.get(slug)) return;
    const last = messages[messages.length - 1];
    const orphaned = last && last.role === 'assistant'
      && !((last.content || '').trim()) && !(last.parts && last.parts.length);
    if (!orphaned) return;
    const id = last.id;
    if (orphanHandled.has(id)) return; // already attached/polled this bubble once — don't loop
    orphanHandled.add(id);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${GW}/sessions/${slug}`, { });
        if (cancelled || runs.get(slug)) return;
        const st = res.ok ? (await res.json())?.session : null;
        if (st && (st.state === 'working' || st.isStreaming)) {
          attachRun(slug, id); // live progress + authoritative reply at the end
          return;
        }
      } catch { /* fall through to history poll */ }
      // Idle (or unknown): the reply may already be saved — poll briefly for it.
      for (let i = 0; i < 10 && !cancelled && !runs.get(slug); i++) {
        try {
          const h: any = await gateway.chatHistory.read(slug);
          const gm: ChatMessage[] = Array.isArray(h?.messages) ? h.messages : [];
          const g = gm.find((m) => m.id === id);
          if (g && (((g.content as string) || '').trim() || (g.parts && g.parts.length))) {
            if (!cancelled) setMessages((prev) => prev.map((m) => m.id === id ? { ...m, content: g.content, parts: g.parts } : m));
            return;
          }
        } catch { /* keep trying */ }
        await new Promise((r) => setTimeout(r, 2500));
      }
      // Session is idle and no reply ever landed → this is a DEAD turn (died on an
      // API error). Clear the empty placeholder + surface a retry-able error so a
      // reload doesn't leave a blank "ghost" bubble forever.
      if (!cancelled && !runs.get(slug)) {
        setMessages((prev) => prev.filter((m) => m.id !== id));
        setError('The previous turn ended without a reply (likely an API timeout or overload). Re-send to continue.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyHydrated, slug, main, messages.length]);

  const run = runs.get(slug);
  const streaming = !!run && !run.done;

  const send = useCallback(async (text: string, images?: ChatImage[], context?: string) => {
    const messageText = text.trim() || (images?.length ? 'What do you see in this image?' : '');
    if (!messageText) return;
    setError(null);
    const hasImages = images && images.length > 0;
    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}`, role: 'user',
      content: hasImages ? `${messageText}\n[${images!.length} image${images!.length > 1 ? 's' : ''} attached]` : messageText,
      timestamp: Date.now(),
    };

    if (runs.get(slug)) {
      // mid-run → STEER, Claude-Code style: the message is injected into the
      // CURRENT turn and the agent course-corrects immediately. The live
      // stream keeps flowing in this same pane.
      setMessages((prev) => [...prev, userMsg]);
      bump((x) => x + 1);
      try {
        const r = await fetch(main ? `${GW}/chat/steer` : `${GW}/sessions/${slug}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(main ? { message: messageText } : { message: messageText, steer: true, ...(hasImages ? { images } : {}) }),
        });
        const d = await r.json();
        if (!d.ok) throw new Error(d.error || 'steer failed');
      } catch {
        // run probably just finished — fall back to queueing as a fresh turn
        const q = queues.get(slug) || [];
        q.push({ text: messageText, images, userMsgId: userMsg.id });
        queues.set(slug, q);
        bump((x) => x + 1);
      }
      return;
    }

    const assistantMsgId = `msg_${Date.now() + 1}`;
    setMessages((prev) => [...prev, userMsg, { id: assistantMsgId, role: 'assistant', content: '', timestamp: Date.now() }]);
    setLastFailed(messageText);
    startRun(slug, messageText, images, assistantMsgId, main, context);
    bump((x) => x + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, main]);

  const abort = useCallback(() => {
    runs.get(slug)?.controller.abort();
    queues.delete(slug);
    fetch(main ? `${GW}/abort` : `${GW}/sessions/${slug}/abort`, { method: 'POST' }).catch(() => {});
  }, [slug, main]);

  const retry = useCallback(() => {
    if (!lastFailed || runs.get(slug)) return;
    setError(null);
    send(lastFailed);
  }, [lastFailed, send, slug]);

  // fresh start: wipe the visible history and release/reopen the live
  // session so its working memory is clean too
  const clear = useCallback(async () => {
    queues.delete(slug);
    runs.get(slug)?.controller.abort();
    setMessages([]);
    setError(null);
    try {
      if (main) {
        await fetch(`${GW}/session/new`, { method: 'POST' });
      } else {
        // /reset wipes the persistent context dir too — plain close+open
        // would RESUME the old conversation by design
        await fetch(`${GW}/sessions/${slug}/reset`, { method: 'POST' });
      }
    } catch { /* next open recreates it */ }
  }, [slug, main, setMessages]);

  // Delete one message so no future session is aware of it. Scrub disk FIRST
  // (authoritative), then drop it locally. Returns {msg, idx} for undo.
  const deleteMessage = useCallback(async (id: string): Promise<{ msg: ChatMessage; idx: number } | null> => {
    const snapshot = messagesRef.current;
    const idx = snapshot.findIndex((m) => m.id === id);
    const removed = idx >= 0 ? { msg: snapshot[idx], idx } : null;
    await removeMessageFromDisk(slug, id);
    setMessages((prev) => prev.filter((m) => m.id !== id));
    // The store above is only what's RENDERED. The model's memory is the SDK
    // context tree — scrub it there too, or the AI still remembers a message
    // that vanished from the screen.
    if (removed) {
      gateway.forgetMessage(
        slug,
        { role: removed.msg.role, text: textPartsOf(removed.msg), timestamp: removed.msg.timestamp },
        { main },
      ).catch(() => {});
    }
    return removed;
  }, [slug, setMessages, main]);

  const restoreMessage = useCallback((msg: ChatMessage, idx: number) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      const next = [...prev];
      next.splice(Math.min(Math.max(idx, 0), next.length), 0, msg);
      return next;
    });
  }, [setMessages]);

  return {
    messages,
    streaming,
    queued: getQueueLength(slug),
    streamText: run && !run.done ? run.text : '',
    streamParts: run && !run.done ? run.parts : [],
    thinking: !!run && run.thinking && !run.done,
    usage: run?.usage ?? null,
    error,
    canRetry: !!lastFailed && !!error,
    historyHydrated,
    send,
    abort,
    retry,
    clear,
    deleteMessage,
    restoreMessage,
  };
}
